<script setup lang="ts">
import { computed, h, onBeforeUnmount, watch } from 'vue'
import { CheckCircleIcon, Loader2Icon, XCircleIcon } from 'lucide-vue-next'
import { useToast } from '@mumble/components'
import useReportExportFetch from '@/js/hooks/api/report/useReportExportFetch'
import useReportExportQueue, { QueuedReportExport } from '@/js/hooks/useReportExportQueue'

interface Props {
    item: QueuedReportExport
}

const props = defineProps<Props>()

// Renderless: polls the export and keeps its toast in sync
const { show, update, hide } = useToast()
const { remove } = useReportExportQueue()
const { reportExport, isError, isTimedOut } = useReportExportFetch(computed(() => props.item.id))

const state = computed(() => {
    if (reportExport.value?.status === 'done') return 'done'
    if (reportExport.value?.status === 'failed' || isError.value || isTimedOut.value) return 'failed'

    return 'pending'
})

const onDownload = () => {
    window.open(`/report?${new URLSearchParams({ ...props.item.query, exportId: props.item.id }).toString()}`, '_blank')
    remove(props.item.id)
}

const onClose = () => remove(props.item.id)

const toastId = show({
    label: 'Generazione report in corso...',
    description: props.item.hasAi ? 'Con valutazione AI: può richiedere da 1 a 3 minuti' : undefined,
    timeout: 0,
    icon: h(Loader2Icon, { class: 'loading-icon' }),
})

watch(state, (value) => {
    if (value === 'done') {
        update(toastId, {
            label: 'Il report è pronto',
            description: undefined,
            type: 'default',
            icon: h(CheckCircleIcon, { class: 'text-green-600' }),
            closable: true,
            onClose,
            actions: [{ label: 'Scarica', primary: true, onClick: onDownload }],
        })
    } else if (value === 'failed') {
        update(toastId, {
            label: 'Impossibile generare il report',
            description: 'Si è verificato un errore durante la generazione. Riprova.',
            icon: h(XCircleIcon, { class: 'text-red-600' }),
            closable: true,
            onClose,
            actions: [],
        })
    }
})

// Leaving the queue (download or close) removes the toast too
onBeforeUnmount(() => hide(toastId))
</script>

<template>
    <!-- Renderless: the UI is the toast -->
    <span hidden />
</template>
